import { useEffect, useMemo, useState } from 'react'
import { TaxonomyTerm } from '../utils'

export function useFilters<Filter extends string | number>(
	validFilterTerms: TaxonomyTerm[] = [],
	initialValue: Filter[] = [],
) {
	const [filtersRaw, setFilters] = useState<Filter[]>(initialValue)

	const filters = useMemo(
		() =>
			filtersRaw.filter(
				f =>
					validFilterTerms.length === 0 ||
					validFilterTerms.some(t => t.slug === f),
			),
		[filtersRaw, JSON.stringify(validFilterTerms)],
	)

	function containsFilter(filter: Filter): boolean {
		return filters.some(f => f === filter)
	}

	function addFilter(filter: Filter) {
		if (!containsFilter(filter)) {
			setFilters(f => [...f, filter])
		}
	}

	function removeFilter(filter: Filter) {
		setFilters(filters => filters.filter(f => f !== filter))
	}

	return {
		filters,
		addFilter,
		containsFilter,
		removeFilter,
		toggleFilter(filter: Filter, force?: boolean) {
			if (force === undefined) {
				force = containsFilter(filter)
			}

			if (force) {
				addFilter(filter)
			} else {
				removeFilter(filter)
			}
		},
	}
}

export function useUrlFilters(): { product: string[]; project: string[] } {
	const queryString = location.hash.substring(1)

	const r = useMemo(() => {
		const query = new URLSearchParams(location.hash.substring(1))

		return {
			product: query.get('product')?.split(',') ?? [],
			project: query.get('project')?.split(',') ?? [],
		}
	}, [queryString])

	return r
}

export function usePushUrlFilters(products: string[], projects: string[]) {
	useEffect(() => {
		const newHash = `product=${products.join(',')}&project=${projects.join(
			',',
		)}`
		location.hash = newHash
	}, [products, projects])
}
